import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import Swiper from 'swiper';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

gsap.registerPlugin(ScrollTrigger, SplitText);

export default class HeroAnim {

	constructor(section) {
		
		this.colorMap = {};
		this.isWorking = false;
		this.hasEntered = false;
		this.container = section.container;
		this.bgkColor = this.container.dataset.color;
		this.section = this.container.querySelector('.hero');
		this.boxes = [...this.section.querySelectorAll('.box')];
		this.slider = this.section.querySelector('.wrapper-cards');
		this.sliderWrapper = this.slider.querySelector('.swiper-wrapper');
		this.slides = [...this.slider.querySelectorAll('.box')];
		this.activeBox = null;
		this.autoplayStopped = false;
		this.extraTiming = document.body.classList.contains('grey') || document.body.classList.contains('red') ? 1 : 0;

		// Elementi di testo dell'hero
		this.heroTitle = this.section.querySelector('.hero-title');
		this.heroIntro = this.section.querySelector('.intro');
		this.heroDates = [...this.section.querySelectorAll('.date')];

		// Elementi del layout di pagina
		this.navbar = document.querySelector('.main-navbar');
		this.overlay = document.querySelector('.transition-overlay');
		this.footer = this.container.querySelector('.main-footer');
		this.pageSections = [...this.container.querySelectorAll('section')];
	}

	init() {
		this.createGradientAnim();
		this.initHpSlider();
	}

	// ─── TRANSIZIONI PAGINA ───────────────────────────────────────────────────


	enter() {
		this.section.style.background = 'inherit';

		const tl = gsap.timeline({
			defaults: { force3D: true, ease: 'power2.out' },
			paused: true,
			onStart: () => {
				this.init();
			}
		});

		if (document.body.classList.contains('grey') || document.body.classList.contains('red')) {

			tl
				.to(document.body, { backgroundColor: this.bgkColor, duration: 1, force3D: false }, 0)
				.add(() => {
					document.body.classList.remove(...document.body.classList);
					document.body.classList.add('hide-layer');
					gsap.set(document.body, { clearProps: 'all' });
					this.section.style.removeProperty('background');
				}, '>');
		}

		tl
			.add(() => { 
				document.body.style.overflow = 'scroll';
			}, 0)
			.set(this.overlay, { autoAlpha: 0 }, 0)
			.add(() => { window.scrollTo(0, 0); }, 0)
			.to([this.heroTitle, this.heroIntro, this.slider, this.heroDates], { autoAlpha: 1, duration: 0.8, stagger: 0.05 }, 0.3 + this.extraTiming)
			.to(this.navbar, { y: '0', opacity: 1, duration: 0.8 }, 0.4)
			.add(() => { window.headerInstance?.initScrollBehavior(); }, '>');

		// SplitText intro solo su desktop, dopo font load
		document.fonts.ready.then(() => {
			const textSplit = window.innerWidth > 768
				? SplitText.create(this.heroIntro, { type: 'words', autoSplit: true, mask: 'words', aria: 'none' })
				: null;

			if (textSplit) tl.add(() => textSplit.revert(), '>');

			tl.play();
		});

		return tl;
	}

	
	exit(type = null) {
		const dur = window.innerWidth > 768 ? 1 : 0.6;

		const tl = gsap.timeline({ defaults: { ease: 'expo.out', force3D: true } });

		// Gestione navbar in base allo stato e al tipo di transizione
		if (type === 'self' || type === 'splash') {
			if (this.navbar.classList.contains('is-fixed')) {
				window.headerInstance?.backToHome();
			} else {
				tl.to(this.navbar, { y: '100%', opacity: 0, duration: dur }, 0);
			}
		} else {
			if (!this.navbar.classList.contains('is-fixed')) {
				tl.to(this.navbar, { y: '100%', opacity: 0, duration: dur }, 0);
			}
		}

		tl
			.to([this.pageSections, this.footer], {
				autoAlpha: 0, duration: dur, ease: 'power4.out'
			}, 0)
			.add(() => {
				window.scrollTo(0, 0);
				if (type === 'splash') document.body.classList.remove('hide-layer');
			}, '>');

		// Overlay rosso solo per la transizione verso splash
		if (type === 'splash') {
			tl
				.set(this.overlay, {
					'--main-bgk':     '#E30613',
					'--in-gradient':  '#E30613',
					'--out-gradient': '#E30613',
					top: '0', left: '0', transform: 'none',
					position: 'absolute',
					'--width': '100vw', '--height': '100vh',
				}, 0.2)
				.to(this.overlay, { autoAlpha: 1, duration: dur * 0.8 }, 0.3)
				.set(this.overlay, { clearProps: 'all' }, '>');
		}

		return tl;
	}

	// ─── ANIMAZIONE GRADIENTE ─────────────────────────────────────────────────

	createGradientAnim() {
		this.boxes.forEach((box, index) => {
			if (box.dataset.color) this.colorMap[index] = box.dataset.color;
		});

		const coloredIndices   = Object.keys(this.colorMap).map(Number);
		const totalSections    = coloredIndices.length;
		const mainColor        = this.section.querySelector('.box').dataset.color;
		const sliderSpeed      = 2500;
		const totalSlides      = this.boxes.length;
		const slidesPerSection = totalSlides / totalSections;
		const sectionDuration  = (slidesPerSection * sliderSpeed) / 1000;

		this.gradientTl = gsap.timeline({
			paused: true,
			repeat: -1,
			defaults: { ease: 'none', force3D: true },
		});

		for (let i = 0; i < totalSections; i++) {
			const isLast       = i === totalSections - 1;
			const currentColor = this.colorMap[coloredIndices[i]];
			const nextColor    = isLast ? mainColor : this.colorMap[coloredIndices[i + 1]];
			const textColor    = nextColor === '#282827' ? '#fff' : '#282827';

			this.gradientTl
				.to(this.section, {
					'--gradient-end': nextColor,
					duration: sectionDuration * 0.2,
				}, '>')
				.to(this.section, {
					'--end-position':   0,
					'--start-position': -100,
					'--gradient-start': nextColor,
					duration: sectionDuration * 0.4,
				}, '>')
				.to([this.heroTitle, this.heroIntro, ...this.heroDates], {
					color: textColor,
					duration: sectionDuration * 0.4,
					ease: 'power2.out',
				}, '<60%')
				.set(this.section, {
					'--end-position':   300,
					'--start-position': 0,
				}, '>' + (sectionDuration * 0.2));
		}
	}

	// ─── AUTOPLAY HELPERS ─────────────────────────────────────────────────────

	stopAutoplay(swiper) {
		this.isWorking  = false;
		this.hasEntered = true;
		gsap.set(this.sliderWrapper, { transitionDuration: 0 });
		gsap.set(this.sliderWrapper, { x: swiper.getTranslate() });
		if (this.gradientTl) this.gradientTl.pause();
	}

	restartAutoplay(swiper) {
		if (this.isWorking) return;
		this.isWorking = true;

		const slide            = swiper.slides[0];
		const slideWidth       = parseFloat(getComputedStyle(slide).width) + parseFloat(getComputedStyle(slide).marginRight);
		const remainingTranslate = -(swiper.translate - swiper.getTranslate());
		const valueTiming      = remainingTranslate * swiper.params.speed / slideWidth;

		gsap.set(this.sliderWrapper, { transitionDuration: valueTiming });
		gsap.set(this.sliderWrapper, { x: swiper.translate });

		setTimeout(() => {
			swiper.autoplay.start();
			if (this.gradientTl) this.gradientTl.play();
			this.isWorking  = false;
			this.hasEntered = false;
		}, valueTiming);
	}

	// ─── ESPANSIONE BOX (solo desktop) ────────────────────────────────────────

	expandBox() {
		const HpSlider = this.slider.swiper;

		this.slides.forEach(box => {
			const boxStandardHeight = box.offsetHeight;
			const maxHeight = (window.innerWidth >= 1024 && window.innerHeight <= 700) ? boxStandardHeight * 1.4 : boxStandardHeight * 2;

			const cta = box.querySelector('.box-cta');

			const expandTl = gsap.timeline({
				paused: true,
				onStart: () => { box.classList.add('expand'); },
				onReverseComplete: () => {
					box.classList.remove('expand');
					if (cta) gsap.set(cta, { opacity: 0, yPercent: 10, pointerEvents: 'none' });

					if (box === this.activeBox) {
						this.activeBox = null;
						const anyBoxHovered = this.slides.some(b => b.matches(':hover'));
						if (!anyBoxHovered) {
							this.restartAutoplay(HpSlider);
							this.autoplayStopped = false;
						}
					}
				},
			});

			// Espansione box
			expandTl.fromTo(box,
				{ height: boxStandardHeight },
				{ height: maxHeight, duration: 0.4, ease: 'power2.out' }
			);

			// CTA: appare durante l'espansione
			if (cta) {
				// opacity (non autoAlpha) evita che visibility:hidden blocchi i click
				gsap.set(cta, { opacity: 0, yPercent: 10, pointerEvents: 'none' });
				expandTl
					.to(cta, {
						opacity: 1,
						yPercent: 0,
						duration: 0.25,
						ease: 'power2.out',
					}, 0.15)
					.set(cta, { pointerEvents: 'auto' }, 0.15);
			}

			box.animation = expandTl;

			box.addEventListener('mouseenter', e => {
				if (window.innerWidth < 600) return;
				e.preventDefault();

				if (!this.autoplayStopped) {
					this.stopAutoplay(HpSlider);
					this.autoplayStopped = true;
				}

				if (this.activeBox) this.activeBox.animation.reverse();

				box.animation.play();
				this.activeBox = box;
			});

			box.addEventListener('mouseleave', e => {
				if (window.innerWidth < 600) return;
				e.preventDefault();
				e.stopPropagation();
				box.animation.reverse();
			});
		});
	}

	// ─── SLIDER ───────────────────────────────────────────────────────────────

	initHpSlider() {
		const HpSlider = new Swiper(this.slider, {
			modules: [Autoplay, Navigation],
			speed: 2500,
			slidesPerView: 3.2,
			centeredSlides: false,
			spaceBetween: 16,
			grabCursor: true,
			allowTouchMove: false,
			loop: true,
			breakpoints: {
				600:  { spaceBetween: 24, slidesPerView: 4.3 },
				900:  { spaceBetween: 24, slidesPerView: 6.3 },
				1360: { spaceBetween: 24, slidesPerView: 7.8 },
			},
			autoplay: {
				delay: 0,
				disableOnInteraction: true,
			},
			on: {
				afterInit: () => {
					setTimeout(() => {
						this.section.style.removeProperty('background');
						this.gradientTl.play();
						if (window.innerWidth >= 1024) this.expandBox();
					}, this.extraTiming * 1000 + 1);
				},
			},
		});

		this.slider.addEventListener('mouseenter', () => {
			if (document.body.classList.contains('no-scroll') || window.innerWidth < 600) return;
			this.stopAutoplay(HpSlider);
		});

		this.slider.addEventListener('mousemove', () => {
			if (document.body.classList.contains('no-scroll') || this.hasEntered || window.innerWidth < 600) return;
			this.stopAutoplay(HpSlider);
		});

		this.slider.addEventListener('mouseleave', () => {
			if (document.body.classList.contains('no-scroll') || window.innerWidth < 600) return;
			this.restartAutoplay(HpSlider);
		});

		HpSlider.on('touchStart', () => {
			if (window.innerWidth > 600) return;
			this.stopAutoplay(HpSlider);
		});

		HpSlider.on('touchEnd', () => {
			if (window.innerWidth > 600) return;
			this.restartAutoplay(HpSlider);
		});
	}
}